import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/context/AuthContext';
import api, { apiError } from '@/lib/api';
import { isProTier, userTierKey } from '@/lib/tier';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { UnitToggle } from '@/components/UnitToggle';
import { UserAvatar } from '@/components/UserAvatar';
import { AchievementsCard } from '@/components/AchievementsCard';
import { IkoCard } from '@/components/IkoCard';
import { fileToAvatarDataUrl } from '@/lib/avatar';
import {
  resolveUnits, weightUnit, displayWeight, weightToKg, weightPlaceholder,
} from '@/lib/units';
import {
  Camera, Save, Loader2, Sparkles, Zap, ArrowDownCircle, CreditCard, Activity,
  CheckCircle2, Clock, Video, User as UserIcon,
} from 'lucide-react';
import { toast } from 'sonner';

const LEVELS = ['beginner', 'intermediate', 'advanced'];

const StatusBadge = ({ status }) => {
  const { t } = useTranslation();
  const paid = status === 'paid';
  return (
    <Badge variant="outline" className={paid ? 'border-[#2EE59D]/40 text-[#2EE59D]' : 'border-white/20 text-muted-foreground'}>
      {paid ? <CheckCircle2 className="w-3 h-3 mr-1" /> : <Clock className="w-3 h-3 mr-1" />}{t(`profile.status.${status}`, { defaultValue: status || 'pending' })}
    </Badge>
  );
};

export default function Profile() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { user, refreshUser } = useAuth();
  const locale = (i18n.resolvedLanguage || i18n.language || 'nl').slice(0, 2);
  const fileRef = useRef(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [units, setUnits] = useState(resolveUnits(user));
  const [name, setName] = useState(user?.name || '');
  const [weightKg, setWeightKg] = useState(user?.weight_kg ?? null);
  const [level, setLevel] = useState(user?.skill_level || 'beginner');
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [downgrading, setDowngrading] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get('/users/me/profile');
      setProfile(data);
      setName(data.user.name || '');
      setWeightKg(data.user.weight_kg ?? null);
      setLevel(data.user.skill_level || 'beginner');
      setUnits(resolveUnits(data.user));
    } catch (e) { console.error('Profile load failed', e); } finally { setLoading(false); }
  };

  useEffect(() => {
    const init = async () => { await load(); };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isPro = isProTier(profile?.user?.subscription_tier);
  const tierK = userTierKey(profile?.user);
  const planBadgeLabel = tierK === 'elite' ? t('profile.elite') : tierK === 'pro' ? t('profile.pro') : t('profile.free');
  const planBadgeClass = tierK === 'elite' ? 'bg-[#FFB020] text-black' : tierK === 'pro' ? 'bg-[#FF6B35] text-black' : 'bg-white/10 text-white';
  const planName = tierK === 'elite' ? t('profile.elitePlan') : tierK === 'pro' ? t('profile.proPlan') : t('profile.freePlan');

  const onUnitChange = (u) => { setUnits(u); if (typeof localStorage !== 'undefined') localStorage.setItem('kiteiq_units', u); };

  const saveProfile = async () => {
    setSaving(true);
    try {
      await api.put('/users/me/preferences', { name, units, weight_kg: weightKg, skill_level: level });
      if (typeof localStorage !== 'undefined') localStorage.setItem('kiteiq_units', units);
      await refreshUser();
      await load();
      toast.success(t('profile.profileUpdated'));
    } catch (e) { toast.error(apiError(e, t('profile.saveError'))); } finally { setSaving(false); }
  };

  const onAvatarPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const dataUrl = await fileToAvatarDataUrl(file, 256, 0.85);
      await api.put('/users/me/avatar', { avatar: dataUrl });
      await refreshUser();
      await load();
      toast.success(t('profile.photoUpdated'));
    } catch (err) { toast.error(apiError(err, err.message || t('profile.photoError'))); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  const downgrade = async () => {
    setDowngrading(true);
    try {
      await api.post('/billing/downgrade');
      await refreshUser();
      await load();
      toast.success(t('profile.downgraded'));
    } catch (e) { toast.error(apiError(e, t('profile.downgradeError'))); } finally { setDowngrading(false); }
  };

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-10 py-10 space-y-6">
        <Skeleton className="h-28 w-full bg-white/10 rounded-2xl" />
        <Skeleton className="h-64 w-full bg-white/10 rounded-2xl" />
      </div>
    );
  }

  const u = profile?.user || user;
  const credits = u?.credits ?? 0;
  const payments = profile?.payments || [];

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-10 py-10">
      <div className="label-eyebrow text-[#48CAE4] mb-2 flex items-center gap-2"><UserIcon className="w-4 h-4" /> {t('profile.account')}</div>
      <h1 className="font-display text-4xl sm:text-5xl">{t('profile.title')}</h1>

      {/* Identity header */}
      <div className="kite-card p-6 mt-6 flex items-center gap-5" data-testid="profile-header">
        <div className="relative">
          <UserAvatar src={u?.avatar} name={u?.name || u?.email} size={84} />
          <button onClick={() => fileRef.current?.click()} disabled={uploading}
            className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-[#00B4D8] text-black flex items-center justify-center hover:bg-[#48CAE4] transition-colors" title={t('profile.photoUpdated')} data-testid="profile-avatar-btn">
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
          </button>
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onAvatarPick} data-testid="profile-avatar-input" />
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            <h2 className="font-display text-3xl truncate" data-testid="profile-name">{u?.name || t('profile.rider')}</h2>
            <Badge className={planBadgeClass} data-testid="profile-plan-badge">{planBadgeLabel}</Badge>
            {u?.is_admin && <Badge variant="outline" className="border-[#FFB020]/40 text-[#FFB020]">{t('profile.admin')}</Badge>}
          </div>
          <p className="text-muted-foreground text-sm mt-1">{u?.email}</p>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mt-6">
        {/* Edit form */}
        <div className="kite-card p-6" data-testid="profile-edit-card">
          <h3 className="font-semibold text-lg mb-4">{t('profile.personalDetails')}</h3>
          <div className="space-y-4">
            <div>
              <Label htmlFor="pf-name">{t('profile.name')}</Label>
              <Input id="pf-name" value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5 bg-[#0A0A0A] border-white/15" data-testid="profile-input-name" />
            </div>
            <div>
              <Label>{t('profile.unitSystem')}</Label>
              <div className="mt-1.5"><UnitToggle value={units} onChange={onUnitChange} testid="profile-unit-toggle" /></div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="pf-weight">{t('profile.weight', { unit: weightUnit(units) })}</Label>
                <Input id="pf-weight" type="number" inputMode="decimal" value={displayWeight(weightKg, units) ?? ''} onChange={(e) => setWeightKg(weightToKg(e.target.value, units))} placeholder={weightPlaceholder(units)} className="mt-1.5 bg-[#0A0A0A] border-white/15" data-testid="profile-input-weight" />
              </div>
              <div>
                <Label>{t('profile.skillLevel')}</Label>
                <Select value={level} onValueChange={setLevel}>
                  <SelectTrigger className="mt-1.5 bg-[#0A0A0A] border-white/15" data-testid="profile-select-level"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#12161B] border-white/15 text-white">{LEVELS.map((l) => <SelectItem key={l} value={l}>{t(`levels.${l}`)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex justify-end">
              <Button onClick={saveProfile} disabled={saving} className="bg-[#00B4D8] hover:bg-[#48CAE4] text-black font-semibold rounded-xl" data-testid="profile-save-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-2" /> {t('profile.save')}</>}
              </Button>
            </div>
          </div>
        </div>

        {/* Subscription + credits */}
        <div className="kite-card p-6" data-testid="profile-subscription-card">
          <h3 className="font-semibold text-lg mb-4">{t('profile.planCredits')}</h3>
          <div className="flex items-center justify-between rounded-xl bg-[#0A0A0A] border border-white/10 p-4">
            <div>
              <div className="label-eyebrow text-muted-foreground">{t('profile.currentPlan')}</div>
              <div className="font-display text-2xl mt-1">{planName}</div>
              {profile?.subscription?.interval && <div className="text-xs text-muted-foreground">{profile.subscription.interval}{profile.subscription.status ? ` · ${profile.subscription.status}` : ''}</div>}
            </div>
            <div className="text-right">
              <div className="label-eyebrow text-muted-foreground flex items-center justify-end gap-1"><Zap className="w-3 h-3 text-[#00B4D8]" /> {t('profile.credits')}</div>
              <div className="font-data text-3xl font-semibold text-[#00B4D8] mt-1" data-testid="profile-credits">{credits}</div>
            </div>
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            {!isPro ? (
              <Button onClick={() => navigate('/pricing')} className="bg-[#FF6B35] hover:bg-[#FFB020] text-black font-semibold rounded-xl" data-testid="profile-upgrade-btn">
                <Sparkles className="w-4 h-4 mr-2" /> {t('profile.upgradeToPro')}
              </Button>
            ) : (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" className="border-white/15 bg-white/5 hover:bg-white/10 rounded-xl" data-testid="profile-downgrade-btn">
                    <ArrowDownCircle className="w-4 h-4 mr-2" /> {t('profile.downgradeToFree')}
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent className="bg-[#12161B] border-white/15 text-white">
                  <AlertDialogHeader>
                    <AlertDialogTitle>{t('profile.downgradeTitle')}</AlertDialogTitle>
                    <AlertDialogDescription className="text-muted-foreground">
                      {t('profile.downgradeDesc', { credits })}
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel className="bg-transparent border-white/15 hover:bg-white/10">{t('profile.keepPro')}</AlertDialogCancel>
                    <AlertDialogAction onClick={downgrade} disabled={downgrading} className="bg-[#FF6B35] hover:bg-[#FFB020] text-black" data-testid="profile-downgrade-confirm">
                      {downgrading ? <Loader2 className="w-4 h-4 animate-spin" /> : t('profile.downgrade')}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
            {tierK === 'pro' && (
              <Button onClick={() => navigate('/pricing')} className="bg-[#FFB020] hover:bg-[#ffc04d] text-black font-semibold rounded-xl" data-testid="profile-upgrade-elite-btn">
                <Sparkles className="w-4 h-4 mr-2" /> {t('pricing.upgradeToElite')}
              </Button>
            )}
            <Button variant="outline" onClick={() => navigate('/pricing')} className="border-white/15 bg-white/5 hover:bg-white/10 rounded-xl" data-testid="profile-buy-credits-btn">
              <Zap className="w-4 h-4 mr-2" /> {t('profile.buyCredits')}
            </Button>
          </div>

          {/* Strava + activity stats */}
          <div className="mt-5 grid grid-cols-2 gap-3">
            <div className="rounded-xl bg-[#0A0A0A] border border-white/10 p-3 flex items-center gap-2">
              <Activity className="w-4 h-4 text-[#FC4C02]" />
              <div className="text-sm">
                <div className="text-muted-foreground text-xs">{t('profile.strava')}</div>
                <button onClick={() => navigate('/dashboard')} className="font-medium hover:text-[#48CAE4]" data-testid="profile-strava-status">{u?.strava_connected ? t('profile.connected') : t('profile.notConnected')}</button>
              </div>
            </div>
            <div className="rounded-xl bg-[#0A0A0A] border border-white/10 p-3 flex items-center gap-2">
              <Video className="w-4 h-4 text-[#48CAE4]" />
              <div className="text-sm">
                <div className="text-muted-foreground text-xs">{t('profile.aiAnalyses')}</div>
                <div className="font-medium">{profile?.stats?.analyses ?? 0}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Achievements */}
      <AchievementsCard />

      {/* IKO certifications */}
      <IkoCard />

      {/* Billing history */}
      <div className="kite-card p-6 mt-6" data-testid="profile-billing-card">
        <h3 className="font-semibold text-lg mb-4 flex items-center gap-2"><CreditCard className="w-5 h-5 text-[#48CAE4]" /> {t('profile.billingHistory')}</h3>
        {payments.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('profile.noPayments')}</p>
        ) : (
          <div className="divide-y divide-white/8">
            {payments.map((p) => (
              <div key={p.id} className="flex items-center justify-between py-3" data-testid="profile-payment-row">
                <div>
                  <div className="font-medium">{p.description}{p.demo ? <span className="text-xs text-muted-foreground"> · {t('profile.test')}</span> : ''}</div>
                  <div className="text-xs text-muted-foreground">{p.created_at ? new Date(p.created_at).toLocaleString(locale, { dateStyle: 'medium', timeStyle: 'short' }) : ''}</div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="font-data font-semibold">€{Number(p.amount_eur || 0).toFixed(2)}</span>
                  <StatusBadge status={p.status} />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
